import type {
  SimulationDependency,
  SimulationExpression,
  SimulationPath,
  SimulationWorkflow
} from '../../simulation.types'
import type { SimpleTask } from 'Automations/nodeDefinitions/conductor'
import type { DependencyCollection } from './dependencyCollection.types'
import { getExpressionDependencies } from './getExpressionDependencies'
import { resolveDependency } from './resolveDependency'
import { taskExecutor } from './taskExecutor'

/**
 * Works out every task we'd have to run to evaluate a path's switches.
 *
 * A switch names the tasks it reads directly, but those tasks usually read
 * others, so we have to follow the chain. Listing only what the switch names
 * looks fine right up until a value turns up undefined at retrieval time.
 *
 *   the switch reads   sequence.customer.id -> getCustomer.output.customer.id
 *   getCustomer reads  data.customerId      -> getQuote.output.quote.customerId
 *   so we need         [getCustomer, getQuote]
 */
export const collectPathDependencies = ({
  expressions,
  workflow,
  optionalDependencyExecutors
}: {
  expressions: SimulationExpression[]
  workflow: SimulationWorkflow
  optionalDependencyExecutors: string[]
}): Pick<SimulationPath, 'dependencies' | 'unsupportedDependencies'> => {
  const unresolvedDependencies = getExpressionDependencies(expressions)

  // Follow every task the switches name, and everything those tasks reach.
  const collected = unresolvedDependencies.reduce<DependencyCollection>(
    (collection, unresolved) =>
      resolveDependency({ collection, unresolved, workflow }),
    { encountered: [], tasks: {}, unsupported: [], visited: [] }
  )

  // Drop anything we couldn't look up, keeping the order we met them in.
  const dependencies = collected.encountered.reduce<SimulationDependency[]>(
    (acc, unresolved) => {
      const task = collected.tasks[unresolved.taskReferenceName]
      if (!task) return acc

      return [
        ...acc,
        {
          task,
          semanticTypes: unresolved.semanticTypes,
          isOptional: isOptionalDependency({
            task,
            optionalDependencyExecutors
          })
        }
      ]
    },
    []
  )

  // A task we met twice is still only one thing to fetch.
  return {
    dependencies: dedupeDependencies(dependencies),
    unsupportedDependencies: [...new Set(collected.unsupported)]
  }
}

/** Whether this task is one of the ones we let answer with nothing. */
const isOptionalDependency = ({
  task,
  optionalDependencyExecutors
}: {
  task: SimpleTask
  optionalDependencyExecutors: string[]
}): boolean => {
  const executor = taskExecutor(task)

  return (
    executor !== undefined && optionalDependencyExecutors.includes(executor)
  )
}

/**
 * Multiple tasks (switches or otherwise) can often read the same
 * task under different semantic types, so we keep every type against the single
 * entry rather than fetching that task twice.
 */
export const dedupeDependencies = (
  dependencies: SimulationDependency[]
): SimulationDependency[] => {
  const merged = dependencies.reduce<Record<string, SimulationDependency>>(
    (acc, dependency) => {
      const existing = acc[dependency.task.taskReferenceName]

      return {
        ...acc,
        [dependency.task.taskReferenceName]: existing
          ? {
              ...existing,
              semanticTypes: [
                ...new Set([
                  ...existing.semanticTypes,
                  ...dependency.semanticTypes
                ])
              ]
            }
          : dependency
      }
    },
    {}
  )

  return Object.values(merged)
}
